import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Badge, theme } from '@frameio/vapor';
import { ListFolderThumb } from '../../../folder';
import { WIDTH as FOLDER_THUMB_WIDTH } from '../../../folder/components/ListFolderThumb';
import { useAssetTypename } from '../../../shared';
import { DraggingThumbnail } from '../Thumbnail';
import { ASSET_WIDTH } from '../Thumbnail/DraggingThumbnail';
import TEXT from './generated/TEXT/TrailAsset';

/**
 * wraps a DraggingThumbnail in an asset context provider and
 * provides container styling. it's used both in single and multi drag overlays
 */
export const AssetForTrail = ({
  id,
  badgeCount,
}: {
  id: string;
  badgeCount?: number;
}) => {
  const isFolder = useAssetTypename() === 'FolderAsset';

  let asset = <DraggingThumbnail assetId={id} />;
  if (isFolder) {
    asset = (
      <div
        style={{
          scale: ASSET_WIDTH / FOLDER_THUMB_WIDTH,
          transformOrigin: '0px 12px',
        }}
      >
        <ListFolderThumb folderId={id} isRestricted={false} />
      </div>
    );
  }

  return (
    <TrailAssetContainer>
      <div
        style={{
          width: ASSET_WIDTH,
          height: ASSET_WIDTH,
        }}
      >
        <MotionContainer>{asset}</MotionContainer>
      </div>
      {badgeCount ? (
        <Badge
          backgroundColor="brand"
          borderColor="panel"
          content={`${badgeCount}`}
          isShowing={true}
          label={TEXT.badgeLabel}
          size={BADGE_SIZE}
          style={{
            position: 'absolute',
            top: -BADGE_SIZE / 3 + (isFolder ? 6 : 0),
            left: `calc(100% - ${BADGE_SIZE / 2}px)`,
          }}
        />
      ) : undefined}
    </TrailAssetContainer>
  );
};

const BADGE_SIZE = 25;

TEXT.DEFINE({
  badgeLabel: 'Selected assets',
});

const TrailAssetContainer = styled.div.attrs({ 'aria-hidden': true })`
  transform-origin: top left;
  position: relative;
`;

const MotionContainer = ({ children }: { children: ReactNode }) => {
  return (
    <motion.div
      animate={{
        opacity: 1,
        scale: 1,
        transition: {
          opacity: { duration: 0.3 },
          scale: {
            damping: 16,
            mass: 1,
            restSpeed: 0.01,
            stiffness: 156,
            type: 'spring',
            velocity: 1,
          },
        },
      }}
      exit={{
        opacity: 0,
        transition: {
          delay: 0,
          duration: 0.2,
          ease: theme.transitions.curves.easeOut,
        },
      }}
      initial={{
        opacity: 0,
        scale: 0,
      }}
      style={{
        transformOrigin: `0px 0px`,
      }}
    >
      {children}
    </motion.div>
  );
};
